<template>
  <div>
    <!-- Retention rules table -->
    <div class="card retention_rule"
         v-if="isGranted([permissions.ACTIVITY_RETENTION_RULE_VIEW, permissions.ACTIVITY_RETENTION_RULE_VIEW_OWN], activity)"
         qid="retention-rules-section"
         id="update_retention_rule"
    >
      <div class="card-body" :class="{'pr-0 pl-0': showOnTask}">
        <div class="row">
          <div class="col-sm-6">
            <div class="float-left">
              <h5 class="mb-2" qid="retention-rules-table-title" v-if="!showOnTask">
                {{ $t('activities.retention_rule.label') }}
              </h5>

              <span v-if="!showOnTask">
                <span class="cursor-pointer task-label-open mr-2"
                      v-if="getTask('update_retention_rule')"
                >
                  <UpdateSingleTaskModal
                      :task="getTask('update_retention_rule')"
                      @reloadContent="reloadContent"
                      :editIcon="true"
                  ></UpdateSingleTaskModal>
                </span>
                <span v-if="getGuide('update_retention_rule')">
                  <GuideButton :guide="getGuide('update_retention_rule')"></GuideButton>
                </span>
              </span>
            </div>

            <div class="validation-info" qid="view-rr-validation" v-if="validation && !showOnTask">
              <span v-if="validation && validation.validation === 1">
                <font-awesome-icon icon="check-circle" class="icon-success"/>
                {{ $t('system.approved') }}
              </span>
              <span v-if="validation && validation.validation === 0">
                <font-awesome-icon icon="times-circle" class="icon-danger"/>
                {{ $t('system.rejected') }}
              </span>
              <span v-if="validation" class="text-muted font-italic">
                {{ $t('system.by') }}
                <span v-if="validation.created_by" qid="view-rr-validation-by">
                  {{ validation.created_by.name }}
                </span>
              </span>
              <span v-if="validation" class="text-muted font-italic">
                {{ $t('system.on') }}
                <span v-if="validation.created_at" qid="view-rr-validation-date">
                  {{ validation.created_at | formatDate }}
                </span>
              </span>
            </div>
          </div>
          <div class="col-sm-6 text-right">
            <ValidationForm
                :object="activity"
                type="retention_rule"
                scope="validate_retention_rule"
                :disabledItems="disabledValidationOptions"
                @validationModalClose="handleValidationModalClose"
                v-if="getClient.gdprAdvancedModule && !showOnTask && isGranted([permissions.ACTIVITY_RETENTION_RULE_VALIDATE, permissions.ACTIVITY_RETENTION_RULE_VALIDATE_OWN], activity)"
            ></ValidationForm>
            <span class="btn btn-success"
                  @click="showCreateRRModal()"
                  qid="retention-rule-create-button"
                  v-if="isGranted([permissions.ACTIVITY_RETENTION_RULE_CREATE, permissions.ACTIVITY_RETENTION_RULE_CREATE_OWN], activity)"
            >
              <font-awesome-icon icon="plus"/> {{ $t('activities.retention_rule.add_new') }}
            </span>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col">
              {{ $t('activities.retention_rule.table.name') }}
            </th>
            <th scope="col" class="text-center">
              {{ $t('activities.retention_rule.table.time_limit') }}
            </th>
            <th scope="col" class="text-center">
              {{ $t('activities.retention_rule.table.attachment') }}
            </th>
            <th scope="col">
              {{ $t('system.last_updated') }}
            </th>
            <th width="130"></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="object in rrTableItems">
              <td>
                <span v-if="object.retention_rule && object.retention_rule.name"
                      @click="openViewRRModal(object)"
                      class="btn btn-link cursor-pointer text-left"
                >
                  {{ object.retention_rule.name }}
                </span>
              </td>
              <td class="text-center">
                <span v-if="object.retention_rule && object.retention_rule.time_limit" class="text-normal">
                  {{ object.retention_rule.time_limit }}
                </span>
              </td>
              <td class="text-center">
                <span v-if="object.retention_rule && object.retention_rule.media && object.retention_rule.media.length">
                  <font-awesome-icon icon="paperclip"/>
                </span>
              </td>
              <td>
                <span class="text-normal">
                  {{ object.updated_at | formatDate }}
                </span>
              </td>
              <td class="text-right">
                <span v-if="object.retention_rule && object.retention_rule.name"
                      @click="openViewRRModal(object)"
                      class="btn btn-view"
                >
                  <font-awesome-icon icon="eye"/>
                </span>
                <span class="btn btn-delete"
                      qid="delete-rr-modal"
                      @click="showRRConfirmDeleteModal(object)"
                      v-if="isGranted([permissions.ACTIVITY_RETENTION_RULE_DELETE, permissions.ACTIVITY_RETENTION_RULE_DELETE_OWN], activity)"
                >
                  <font-awesome-icon icon="trash-alt" />
                </span>
              </td>
            </tr>
            <tr v-if="!rrLoading && !rrTableItems.length" qid="no-results-row-rr" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
        <br>
        <div class="text-center mb-4" v-if="!showOnTask">
          <span class="cursor-pointer"
                @click="acValidationShow = !acValidationShow"
                qid="expand-rr-validations"
          >
            <span class="mr-2">
              {{ $t('system.view_validation_log') }}
            </span>
            <font-awesome-icon icon="chevron-down" v-if="!acValidationShow"/>
            <font-awesome-icon icon="chevron-up" v-if="acValidationShow"/>
          </span>
        </div>
        <hr class="mb-0" v-show="acValidationShow && !showOnTask">
        <div v-show="acValidationShow && !showOnTask">
          <ValidationList
              :object="activity"
              type="retention_rule"
              :reloadList="updateValidationList"
              @validationListUpdated="setValidation"
          ></ValidationList>
        </div>
      </div>
    </div>


    <!-- Delete retention rule modal template -->
    <b-modal ref="delete-rr" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-rr-title">
          {{ $t('activities.retention_rule.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.retention_rule.delete.description') }}
        <span v-if="itemToDelete.retention_rule">{{ itemToDelete.retention_rule.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('activities.retention_rule.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideRRConfirmDeleteModal"
                  qid="delete-rr-cancel-button"
        >
          {{ $t('activities.retention_rule.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteRR(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-rr-submit-button"
        >
          {{ $t('activities.retention_rule.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Add new retention rule -->
    <b-modal ref="add-retention-rule" hide-footer @hide="hideCreateRRModal">
      <div class="d-block text-center">
        <h4 qid="add-ac-title">
          {{ $t('activities.retention_rule.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="acFormSubmit" qid="create-ac-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-ac-pg-label">
              {{ $t('activities.retention_rule.create.rr_label') }}
            </label>
            <multiselect
                v-model="selectedRR"
                :options="rrItems"
                :placeholder="$t('activities.retention_rule.create.rr_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="loadRR"
                track-by="slug"
                qid="create-rr-form-rr"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedRR && selectedRR.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showRRDescription = !showRRDescription"
                    qid="expand-rr-description"
              >
                {{$t('activities.retention_rule.create.rr_description_expand')}}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showRRDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showRRDescription"/>
            </span>
          </div>
          <div v-if="selectedRR && selectedRR.description && showRRDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedRR.description }}
            </span>
          </div>
          <div v-if="selectedRR && selectedRR.time_limit">
            <label class="mt-3">
                {{ $t('activities.retention_rule.create.time_limit_label') }}
            </label>
            <br>
            <span v-if="selectedRR">
              {{ selectedRR.time_limit }}
            </span>
          </div>
          <br>
          <b-form-group v-if="selectedRR && selectedRR.media && selectedRR.media.length">
            <label qid="create-rr-attachments-label">
              {{ $t('maintenance.third_parties.create.attachments') }}
            </label>
            <br>
            <p v-for="file in selectedRR.media" :key="file.file_name">
              <span class="mr-3 float-right text-muted cursor-pointer"
                    @click="downloadMedia(file)"
                    qid="download-media"
              >
                <font-awesome-icon icon="cloud-download-alt" />
              </span>
              <a @click="downloadMedia(file)" qid="create-rr-media-item">
                {{ file.file_name }}
              </a>
            </p>
          </b-form-group>
          <b-form-group v-if="selectedRR && selectedRR.links && selectedRR.links.length">
            <label qid="create-rr-link-label">
              {{ $t('maintenance.third_parties.create.links_label') }}
            </label>
            <p v-for="(link, index) in selectedRR.links">
              <a :href="link.url" target="_blank" qid="create-rr-link-item">
                {{ link.name ? link.name : link.url }}
              </a>
            </p>

          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateRRModal"
                    qid="create-rr-cancel-button"
          >
            {{ $t('activities.retention_rule.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="create-rr-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('activities.retention_rule.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <RetentionRuleViewModal
        :object="selectedRetentionRule"
        :showModal="viewRetentionRuleModal"
        @closeViewModal="handleCloseViewRRModal"
    ></RetentionRuleViewModal>

  </div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import ValidationList from '@/views/Validations/ValidationList.vue';
  import ValidationForm from '@/views/Validations/ValidationForm.vue';
  import RetentionRuleViewModal from '@/views/Activities/RetentionRuleViewModal.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'RetentionRulesTable',
    data: function() {
      return {
        acForm: {
          name: ''
        },
        error: '',
        loadingItems: false,
        selectedRR: [],
        viewRRModal: false,
        editRRModal: false,
        selectedRRSort: '',
        selectedRRDirection: '',
        acValidationShow: false,
        rrTableItems: [],
        rrLoading: true,
        rrItems: [],
        updateValidationList: false,
        itemToDelete: {},
        itemObject: {},
        reCertificationOptions: [],
        provisioningOptions: [],
        selectedRC: [],
        selectedP: [],
        validationOptions: [],
        validation: undefined,
        disabledValidationOptions: [],
        showRRDescription: false,
        buttonDisabled: false,
        selectedRetentionRule: {},
        viewRetentionRuleModal: false,
        myGuides: [],
        myTasks: []
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      },
      showOnTask: {},
      task: {}
    },
    components: {
      Avatar,
      ValidationList,
      ValidationForm,
      RetentionRuleViewModal,
      UpdateSingleTaskModal,
      GuideButton
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getReloadContent() {
        return this.$store.state.updateContent
      },
      getReloadTasks() {
        return this.$store.state.reloadTasks
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadRR();
      },
      object: function (value) {
        this.activity = value
        this.loadTasks('update_retention_rule');
        this.loadRetentionRules();
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.activity.id && this.$store.state.updatedObject.who_am_i == this.activity.who_am_i) {
              this.activity = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      },
      getReloadTasks: function () {
        if (!this.showOnTask) {
          this.loadTasks('update_retention_rule')
        }
      },
    },
    methods: {
      setValidation(object) {
        this.validation = object
        this.updateValidationList = false;
      },
      reloadContent() {
        this.loadRetentionRules();
        this.loadTasks('update_retention_rule');
      },
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.activity.who_am_i,
              model_id: this.activity.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      handleCloseViewRRModal () {
        this.viewRetentionRuleModal = false;
      },
      openViewRRModal(object) {
        this.selectedRetentionRule = object
        this.viewRetentionRuleModal = true
      },
      downloadMedia(object){
        piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = object.file_name;

          link.click();
          link.remove()
          window.URL.revokeObjectURL(url);
        }, function() {});
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Activity' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Activity',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      showCreateRRModal() {
        this.$refs['add-retention-rule'].show()
      },
      hideCreateRRModal() {
        this.error = ''
        this.selectedRR = []
        this.loadContent()
        this.$refs['add-retention-rule'].hide()
      },
      showRRConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-rr'].show()
      },
      hideRRConfirmDeleteModal() {
        this.itemToDelete = {}
        this.loadContent()
        this.$refs['delete-rr'].hide()
      },
      handleValidationModalClose() {
        this.updateValidationList = true
        this.loadContent()
      },
      confirmDeleteRR(object) {
        let self = this;
        piincHttp.delete('activities/' + this.activity.slug + '/retention-rules/' + object.id).then(function() {
          self.hideRRConfirmDeleteModal();
          self.loadRetentionRules();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('activities.retention_rule.delete.success'))
        }, function() {});
      },
      acFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.selectedRR && this.selectedRR.id) {
          formData.append("retention_rule_id", this.selectedRR.id);
        }

        formData.append("activity", this.activity.slug);

        piincHttp.post('/activities/' + this.activity.slug + '/retention-rules', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.$toastr('success', self.$t('activities.retention_rule.create.success'))
          self.hideCreateRRModal();
          self.loadRetentionRules();
          self.$emit('updated');
          self.loadTasks('update_retention_rule');
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.retention_rule_id) {
              self.error += ' ' + error.data.errors.retention_rule_id[0]
            }
          }
        })
      },
      navigateToTask(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
        this.$router.push({ name: 'activities.view', params: { slug: object.model.slug }, query: { task: object.id }})
      },
      loadContent() {
        let self = this;
        piincHttp.get('activities/' + this.activity.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.activity = response.data;
        }, function() {});
      },
      loadRetentionRules() {
        let self = this;
        piincHttp.get('activities/' + this.activity.slug + '/retention-rules', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.rrTableItems = response.data;
          self.rrLoading = false;
        }, function() {});
      },
      loadRR(query) {
        let self = this;
        piincHttp.get('retention-rules', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.rrItems = response.data.items
          if (self.rrItems && !self.rrItems.length && !query) {
            self.rrItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
          if (self.rrItems && self.rrItems.length) {
            self.validationOptions = _.cloneDeep(self.rrItems[0].validationOptions)
          }
        }, function() {});
      },
    },
    created () {
      this.activity = this.object
      if (this.getClient && this.getClient.slug) {
        this.loadRR();
      }
      if (this.activity && this.activity.slug) {
        this.loadTasks('update_retention_rule');
        this.loadRetentionRules();
      }

      this.loadGuides('update_retention_rule');

    }
  }
</script>
